import { ICourseForSelector } from '@sdk/contracts';
import { Button, Input, Select } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CounterWrapper,
  DropdownWrapper,
  ItemTextWrapper,
  ItemWrapper,
  OptionsWrapper,
  Wrapper
} from './CourseSearchFilter.styles';
import { LabelWrapper } from './CourseSearchFilter.styles';

import { Icon } from '@/components/Icon';

type Props = {
  value?: string[] | string;
  placeholder?: string;
  mode?: 'multiple' | 'tags';
  onChange?: (value?: string[] | string) => void;
  disabled?: boolean;
  block?: boolean;
  courses: ICourseForSelector[];
};

const DROPDOWN_WIDTH = 300;
const INPUT_WIDTH = 120;

export const CourseSearchFilter: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  mode,
  disabled = false,
  block = false,
  courses
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const options = useMemo(
    () =>
      courses
        ?.filter(({ name }) => name.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
        .map(({ _id, name }) => ({
          label: name,
          value: _id
        })),
    [courses, search]
  );

  const handleSelectOption = (option: string) => {
    if (mode !== 'multiple') {
      if (value === option) {
        onChange?.(undefined);
      } else {
        onChange?.(option);
      }
    } else {
      if (value && Array.isArray(value)) {
        if (value.includes(option)) {
          onChange?.(value.filter((val: string) => val !== option));
        } else {
          onChange?.([...value, option]);
        }
      } else {
        onChange?.([option]);
      }
    }
  };

  return (
    <Wrapper $hasValue={!!value && value.length > 0}>
      <Select
        allowClear
        value={value}
        options={options}
        onChange={onChange}
        styles={{ popup: { root: { width: DROPDOWN_WIDTH } } }}
        labelRender={() => {
          return (
            <LabelWrapper>
              <div>{t('COURSES')}</div>
              {value && (
                <CounterWrapper $isDisabled={disabled}>
                  {Array.isArray(value) ? value.length : 1}
                </CounterWrapper>
              )}
            </LabelWrapper>
          );
        }}
        style={{ maxWidth: block ? '100%' : INPUT_WIDTH, width: block ? '100%' : 'auto' }}
        suffixIcon={<Icon i="CaretDown" />}
        placeholder={placeholder}
        dropdownRender={() => {
          return (
            <DropdownWrapper onClick={e => e.stopPropagation()}>
              <Input
                allowClear
                placeholder={placeholder ?? t('SELECT_COURSE')}
                onChange={e => setSearch(e.target.value)}
              />
              {value && (
                <div>
                  <Button type="text" onClick={() => onChange?.(undefined)}>
                    {t('CLEAR_SELECTION')}
                  </Button>
                </div>
              )}
              <OptionsWrapper>
                {options?.map(({ label, value: val }) => {
                  const isSelected =
                    value && Array.isArray(value) ? value.includes(val) : value === val;
                  return (
                    <ItemWrapper
                      key={val}
                      $isSelected={isSelected}
                      onClick={() => handleSelectOption(val)}>
                      <ItemTextWrapper title={label}>{label}</ItemTextWrapper>
                      {isSelected && <Icon i="Checkmark" color="green7" />}
                    </ItemWrapper>
                  );
                })}
              </OptionsWrapper>
            </DropdownWrapper>
          );
        }}
        disabled={disabled}
      />
    </Wrapper>
  );
};
